import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import { isEqual } from 'lodash'

import Portal from 'components/Shared/Portal'
import Modal from 'components/Shared/Modal'
import LocationForm from './LocationForm'
import RangeLocationsActions from './RangeLocationsActions'
import LocationsSummary from './LocationsSummary'
import { useTargeting } from '../TargetingContext'
import { useRangeLocations, useRangeLocationsDispatch } from './RangeLocationsContextProvider'
import { Prompt } from './Prompt'
import { actions, getRadiusVariations, initialState } from './rangeLocationsReducer'
import { withCampaignWideTargetingFallback } from '../targeting'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'

export default function RangeLocations({
  tc,
  isOpen,
  setOpen,
  onSave,
  hasTargetingLimitations = false,
  isFirstTc = false,
}) {
  const { t } = useTranslation()
  const [showPrompt, setShowPrompt] = useState(null)
  const { value: targeting } = useTargeting()
  const { setup } = useCampaignSetup()
  const { locations } = useRangeLocations()
  const dispatch = useRangeLocationsDispatch()
  const { hasUnsavedChanges, hasUnsavedChangesWithExistingData, hasDeleted } = useMemo(() => {
    const tcTargeting = withCampaignWideTargetingFallback(targeting.find((x) => x.tc === tc), setup, isFirstTc)
    if (!tcTargeting) {
      return {
        hasDeleted: false,
        hasUnsavedChangesWithExistingData: false,
        hasUnsavedChanges: false,
      }
    }

    const formatLocationKey = (location) => {
      const qualifierKey = location.qualifier === 'exclude' ? 'exclude' : 'include'
      if (location.data.type === 'city') {
        return `${location.data.key}-${getRadiusVariations(location.radius)?.mile_range}-${qualifierKey}`
      } else if (location.data.type === 'country' || location.data.type === 'country_group') {
        return `${location.data.key}-${qualifierKey}`
      }
    }

    const savedLocationKeys = initialState(tcTargeting).locations.map(formatLocationKey)
    const currentLocationKeys = locations.map(formatLocationKey)

    const hasDeleted = savedLocationKeys.length > currentLocationKeys.length
    const hasUnsavedChanges = !isEqual(savedLocationKeys.sort(), currentLocationKeys.sort())
    const hasUnsavedChangesWithExistingData = hasUnsavedChanges && savedLocationKeys.length > 0

    return { hasUnsavedChanges, hasUnsavedChangesWithExistingData, hasDeleted }
  }, [locations, targeting, tc, setup, isFirstTc])
  const onClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowPrompt(true)
    } else {
      setOpen(false)
    }
  }, [setOpen, hasUnsavedChanges])

  const saveAndClose = useCallback(
    (locations) => {
      onSave(locations)
      setOpen(false)
    },
    [onSave, setOpen],
  )

  return (
    <div className="range-locations">
      {isOpen && !showPrompt && (
        <Portal wrapperId="app-portal">
          <Modal
            closeCallback={onClose}
            title={t('Recommendations.geoTargeting.rangeLocations.title')}
            headerRenderer={({ title, className }) => (
              <h4 className={cn(className, 'mt-3 mr-3')}>{title}</h4>
            )}
            offWhiteBg
            size="lg"
            fullWidth={false}
            onClickOutside={onClose}
            mainContent={
              <div className="range-locations">
                <div className="range-locations_content">
                  <div className="my-3">
                    <p className="fs-16">
                      {t('Recommendations.geoTargeting.rangeLocations.description')}
                    </p>

                    {hasTargetingLimitations && <p className="fs-16 mt-1">
                      {t('Recommendations.geoTargeting.rangeLocations.description_limitations')}
                    </p>}
                  </div>
                  
                  <LocationForm hasTargetingLimitations={hasTargetingLimitations} />

                  <LocationsSummary hasTargetingLimitations={hasTargetingLimitations}/>

                  <RangeLocationsActions onSave={() => saveAndClose()} />
                </div>
              </div>
            }
          />
        </Portal>
      )}
      <Prompt
        isOpen={!!showPrompt}
        forDeleted={hasDeleted}
        forUnsavedChangesWithExistingData={hasUnsavedChangesWithExistingData}
        onYes={() => {
          setShowPrompt(null)
          const tcTargeting = withCampaignWideTargetingFallback(targeting.find((x) => x.tc === tc) ?? { tc }, setup, isFirstTc)
          const { locations: savedLocations } = initialState(tcTargeting)
          const locationsToKeep = savedLocations.filter((x) =>
            locations.some((y) => y.data.key === x.data.key),
          )
          dispatch({ type: actions.reset, payload: { tcTargeting, locations: locationsToKeep } })
          saveAndClose(locationsToKeep)
        }}
        onNo={() => {
          setShowPrompt(null)

          // restore locations from targeting, and add them to the state
          const tcTargeting = withCampaignWideTargetingFallback(targeting.find((x) => x.tc === tc) ?? { tc }, setup, isFirstTc)
          const { locations: savedLocations } = initialState(tcTargeting)
          // restore saved locations that do not exist in the current state
          const missingLocations = savedLocations.filter(
            (x) => !locations.some((y) => y.data.key === x.data.key),
          )
          if (missingLocations.length > 0) {
            dispatch({
              type: actions.reset,
              payload: { tcTargeting, locations: [...locations, ...missingLocations] },
            })
          }
        }}
      />
    </div>
  )
}

RangeLocations.propTypes = {
  tc: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  setOpen: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  hasTargetingLimitations: PropTypes.bool,
  isFirstTc: PropTypes.bool,
}
